import { useState, useEffect } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

interface MenuItem {
  id: string;
  menu_name: string;
  route: string;
  sort_order: number;
}

export default function UserMenus() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const response = await request.get("menus/all");
    setMenus(response.data.data || []);
  };

  const loadUserMenus = async (userId: string) => {
    const assigned = await request.get(`users/${userId}/menus`);
    setSelected((assigned.data.data || []).map((m: any) => m.id));
    const userResp = await request.get(`users/${userId}`);
    setUserName(userResp.data.data?.name || "");
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadAll();
        if (id) {
          await loadUserMenus(id as string);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggle = (menuId: string) => {
    setSelected((prev) =>
      prev.includes(menuId) ? prev.filter((m) => m !== menuId) : [...prev, menuId],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await request.put(`users/${id}/menus`, { menu_ids: selected });
      if (response?.data?.status) {
        toast.success("Menus assigned successfully!");
        navigate("/admin/users");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={userName ? `Assign Menus - ${userName}` : "Assign Menus"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/users"),
              }}
              primaryButton={{
                label: saving ? "Saving..." : "Save Menus",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: handleSave,
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Typography sx={{ fontWeight: 600, color: "#1a1f3a", mb: 1 }}>
                Menu Access
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 1,
                }}
              >
                {menus.map((menu) => (
                  <FormControlLabel
                    key={menu.id}
                    control={
                      <Checkbox
                        checked={selected.includes(menu.id)}
                        onChange={() => handleToggle(menu.id)}
                      />
                    }
                    label={menu.menu_name}
                  />
                ))}
                {menus.length === 0 && (
                  <Typography sx={{ color: "#94a3b8" }}>No menus available.</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}